import { useRef } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Checkbox } from '@/components/ui/Checkbox';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import {
  calendarIssueTypeLabel,
  CALENDAR_ISSUE_OPTIONS,
  isCalendarIssueType,
  showLocalEventsOption,
} from './feedback-calendar-issue.hooks';

import type { CalendarIssueType } from './feedback.types';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_EXTENSIONS = new Set(['.ics', '.json', '.csv']);

interface IssueTypeProps {
  issueType: CalendarIssueType | null;
  onIssueTypeChange: (value: CalendarIssueType) => void;
}

interface AttachmentsProps {
  issueType: CalendarIssueType | null;
  includeLocalEvents: boolean;
  onIncludeLocalEventsChange: (value: boolean) => void;
  uploadedFile: File | null;
  onFileChange: (file: File | null) => void;
  isUploading: boolean;
}

export function FeedbackCalendarIssueType({
  issueType,
  onIssueTypeChange,
}: IssueTypeProps) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        id="calendar-issue-type"
        variant="outline"
        className="h-10 w-full bg-background transition-colors hover:bg-surface-hover focus-ring"
      >
        <span className="sr-only">Calendar issue type</span>
        <span
          className={cn(
            'truncate',
            issueType ? 'text-foreground' : 'text-foreground-muted'
          )}
        >
          {issueType
            ? calendarIssueTypeLabel(issueType)
            : 'Select an issue type...'}
        </span>
        <Icons.chevronDown className="size-4 shrink-0 text-foreground-muted" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align="start"
        sideOffset={8}
        className="w-[var(--radix-dropdown-menu-trigger-width)]"
      >
        <DropdownMenu.RadioGroup
          value={issueType ?? ''}
          onValueChange={(value) => {
            if (isCalendarIssueType(value)) onIssueTypeChange(value);
          }}
        >
          {CALENDAR_ISSUE_OPTIONS.map(([value, label]) => (
            <DropdownMenu.RadioItem key={value} value={value} className="pr-8">
              {label}
            </DropdownMenu.RadioItem>
          ))}
        </DropdownMenu.RadioGroup>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export function FeedbackCalendarIssueAttachments({
  issueType,
  includeLocalEvents,
  onIncludeLocalEventsChange,
  uploadedFile,
  onFileChange,
  isUploading,
}: AttachmentsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!file) {
      onFileChange(null);
      return;
    }
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.has(ext)) {
      toast.error('Only .ics, .json, and .csv files are supported');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('File must be smaller than 10 MB');
      return;
    }
    onFileChange(file);
  };

  return (
    <YStack className="gap-3 w-full">
      {showLocalEventsOption(issueType) && (
        <XStack className="items-center gap-2">
          <Checkbox
            id="include-local-events"
            checked={includeLocalEvents}
            onCheckedChange={(checked) =>
              onIncludeLocalEventsChange(checked === true)
            }
          />
          <label
            htmlFor="include-local-events"
            className="text-sm text-foreground-muted cursor-pointer select-none"
          >
            Include my local calendar events to help debug the issue
          </label>
        </XStack>
      )}

      <YStack className="gap-1.5">
        <XStack className="gap-3 items-center justify-between w-full">
          <span className="text-sm text-foreground-muted">
            Optionally upload calendar data (.ics or .json)
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".ics,.json,.csv"
            className="hidden"
            onChange={handleFileChange}
            aria-label="Upload calendar data file"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className={cn(
              'flex shrink-0 items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs text-foreground-muted',
              'hover:border-foreground-muted hover:text-foreground transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            <Icons.upload className="size-3.5" />
            {uploadedFile ? 'Replace file' : 'Choose file'}
          </button>
        </XStack>

        {uploadedFile && (
          <XStack className="items-center gap-1 text-xs text-foreground-muted min-w-0">
            <Icons.textFile className="size-3.5 shrink-0" />
            <span className="truncate">{uploadedFile.name}</span>
            <button
              type="button"
              onClick={() => onFileChange(null)}
              aria-label="Remove file"
              className="ml-0.5 text-foreground-muted hover:text-foreground shrink-0"
            >
              <Icons.cancel className="size-3" />
            </button>
          </XStack>
        )}
      </YStack>
    </YStack>
  );
}
